import {
  useState } from "react";
import { Grid,
  List } from "lucide-react";

import {
  Button,
  SupplierCard,
  SupplierTable,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
  SupplierFilter
} from "@b2b/ui-components";
import electronicsImage from "../assets/electronics-1.jpg";
import { useAuth } from "../auth/Authprovider";
import { useChatUnreadHeader } from "../chat/ChatUnreadHeaderProvider";

const SupplierListPage = () => {
  const { logout } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");

  // Mock supplier data
  const suppliers = [
    {
      id: 1,
      name: "Shenzhen Auto Parts Manufacturing Co., Ltd.",
      logo: "/api/placeholder/64/64?text=SAP",
      country: "China",
      countryFlag: "🇨🇳",
      yearsInBusiness: 12,
      verified: true,
      rating: 4.8,
      reviewCount: 156,
      businessType: "Manufacturer",
      mainProducts: [
        "Automotive Fuse Blocks",
        "Electrical Components",
        "Wiring Harnesses",
      ],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "95%",
      responseTime: "< 2 hours",
    },
    {
      id: 2,
      name: "Premium Packaging Solutions Ltd.",
      logo: "/api/placeholder/64/64?text=PPS",
      country: "India",
      countryFlag: "🇮🇳",
      yearsInBusiness: 8,
      verified: true,
      rating: 4.6,
      reviewCount: 89,
      businessType: "Exporter",
      mainProducts: ["Magnetic Boxes", "Custom Packaging", "Retail Packaging"],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "92%",
      responseTime: "< 4 hours",
    },
    {
      id: 3,
      name: "Tech Diagnostics International",
      logo: "/api/placeholder/64/64?text=TDI",
      country: "Germany",
      countryFlag: "🇩🇪",
      yearsInBusiness: 15,
      verified: true,
      rating: 4.9,
      reviewCount: 234,
      businessType: "Manufacturer",
      mainProducts: [
        "OBD2 Scanners",
        "Diagnostic Tools",
        "Automotive Software",
      ],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "98%",
      responseTime: "< 1 hour",
    },
    {
      id: 4,
      name: "LED Auto Solutions Corp.",
      logo: "/api/placeholder/64/64?text=LAS",
      country: "United States",
      countryFlag: "🇺🇸",
      yearsInBusiness: 6,
      verified: false,
      rating: 4.4,
      reviewCount: 67,
      businessType: "Wholesaler",
      mainProducts: ["LED Headlights", "Car Lighting", "Automotive Bulbs"],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "87%",
      responseTime: "< 6 hours",
    },
    {
      id: 5,
      name: "Istanbul Industrial Machinery Co.",
      logo: "/api/placeholder/64/64?text=IIM",
      country: "Turkey",
      countryFlag: "🇹🇷",
      yearsInBusiness: 20,
      verified: true,
      rating: 4.7,
      reviewCount: 312,
      businessType: "Manufacturer",
      mainProducts: [
        "Industrial Equipment",
        "Manufacturing Tools",
        "Heavy Machinery",
      ],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "94%",
      responseTime: "< 3 hours",
    },
    {
      id: 6,
      name: "Mumbai Electronics Export Hub",
      logo: "/api/placeholder/64/64?text=MEE",
      country: "India",
      countryFlag: "🇮🇳",
      yearsInBusiness: 10,
      verified: true,
      rating: 4.5,
      reviewCount: 145,
      businessType: "Exporter",
      mainProducts: [
        "Consumer Electronics",
        "Mobile Accessories",
        "Electronic Components",
      ],
      productImages: [
        electronicsImage,
        electronicsImage,
        electronicsImage,
        electronicsImage,
      ],
      responseRate: "90%",
      responseTime: "< 5 hours",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <BizcoverIndiaFigmaHeader />
      <SupplierFilter />
      <main className="container mx-auto px-4 py-8">
        {/* View Toggle and Results Info */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-foreground">Suppliers</h1>
            <span className="text-sm text-gray-500">1,247 results found</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 mr-2">View:</span>
            <Button
              variant={viewMode === "grid" ? "primary" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="px-3"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "primary" : "outline"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="px-3"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Dynamic View Content */}
        <div className="mb-8">
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {suppliers.map((supplier) => (
                <SupplierCard key={supplier.id} supplier={supplier} />
              ))}
            </div>
          ) : (
            <SupplierTable suppliers={suppliers} />
          )}
        </div>

        {/* Pagination */}
        {viewMode == "grid" && (
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive>
                    1
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">2</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">25</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext href="#" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </main>
    </div>
  );
};

export default SupplierListPage;
